import React, { useEffect, useRef, useState } from "react";

/* The wait before a bid/no-bid card exists.
 *
 * Extraction plus scoring is minutes, not seconds, and a spinner over that
 * length of time reads as a hang. What actually shortens a wait is not speed —
 * it is knowing where you are in it.
 *
 * Two rules hold this together:
 *
 * A REAL BAR ONLY WHERE THERE IS A REAL NUMBER. Extraction reports documents
 * settled out of documents total, so it gets a percentage. Scoring is one
 * synchronous call that reports nothing until it returns, so it gets an
 * indeterminate sweep and a clock. A bar that reaches 90% and stalls is worse
 * than no bar, because the second time someone sees it they stop believing any
 * of it.
 *
 * SHOW THE NUMBER WHILE IT MOVES. The requirement count used to appear only
 * once extraction FINISHED, so the step people watch longest sat there with
 * nothing changing on it — reading as stuck while it was in fact working. It
 * now ticks up live, which is the whole difference between a wait and a hang.
 */

const STEPS = [
  { key: "checking",   label: "Checking documents",   done: "Documents checked" },
  { key: "extracting", label: "Reading the RFP",      done: "Requirements extracted" },
  { key: "triaging",   label: "Choosing what to score", done: "Chose what to score" },
  { key: "scoring",    label: "Scoring against your knowledge base", done: "Scored" },
];

const ORDER = { checking: 0, extracting: 1, triaging: 2, scoring: 3, ready: 4 };

/* Counts climb to their new value instead of jumping to it.
 *
 * The requirement total arrives in steps — one document finishes and it leaps
 * 0 -> 92 -> 147. A number that jumps twice in two minutes looks like a static
 * label between the jumps; a number that rolls is unmistakably alive, and it is
 * the same number either way. Runs on rAF and always lands exactly on target. */
const useCountUp = (target, ms = 700) => {
  const [shown, setShown] = useState(target || 0);
  const fromRef = useRef(target || 0);

  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target) || 0;
    if (from === to) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      fromRef.current = to;
      setShown(to);
      return undefined;
    }
    let raf;
    const started = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - started) / ms);
      // easeOutCubic — quick off the mark, settles rather than stops dead.
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);

  return shown;
};

const Tick = () => (
  <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* Indeterminate. Used where the server tells us nothing but time is passing —
 * it says "working", which is true, and claims no proportion, which would not
 * be. */
const Sweep = () => (
  <div aria-hidden="true" className="mt-2 h-[3px] overflow-hidden rounded-full bg-[#18101C]/[0.06]">
    <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-transparent via-[#A04A97] to-transparent animate-copilot-sweep motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-40" />
  </div>
);

const CopilotQualifyProgress = ({ stage, documents, requirements, scoringTotal = 0, excludedCount = 0 }) => {
  const current = ORDER[stage] ?? 0;

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setElapsed((n) => n + 1);
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const total = documents?.total || 0;
  const settled = (documents?.ready || 0) + (documents?.failed || 0);
  const pct = total > 0 ? Math.round((settled / total) * 100) : 0;
  const found = Number(requirements) || 0;
  const shownFound = useCountUp(found);

  return (
    <div>
      <ol className="space-y-0">
        {STEPS.map((s, i) => {
          const isDone = i < current;
          const isNow = i === current;
          const isLast = i === STEPS.length - 1;

          return (
            <li key={s.key} className={`relative flex gap-3 ${isLast ? "" : "pb-4"}`}>
              {/* A rail between the markers, so this reads as one process
                  moving rather than three unrelated lines. */}
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`absolute left-[7px] top-[18px] bottom-[2px] w-px ${
                    isDone ? "bg-[#2DAD4E]/35" : "bg-[#18101C]/[0.10]"
                  }`}
                />
              )}

              <span className="relative z-10 mt-[1px] flex h-4 w-4 flex-none items-center justify-center bg-white">
                {isDone ? (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#2DAD4E] text-white animate-copilot-rise motion-reduce:animate-none">
                    <Tick />
                  </span>
                ) : isNow ? (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#A04A97] opacity-60 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#A04A97]" />
                  </span>
                ) : (
                  <span className="h-2 w-2 rounded-full bg-[#D8D3DC]" />
                )}
              </span>

              <div className={`min-w-0 flex-1 ${i > current ? "opacity-40" : ""} transition-opacity duration-500 motion-reduce:transition-none`}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className={`text-[13.5px] leading-snug ${isNow ? "font-semibold text-[#18101C]" : "font-medium text-[#6B6472]"}`}>
                    {isDone ? s.done : s.label}
                  </p>

                </div>

                {/* Extraction: a real percentage, and the count climbing beside
                    it so the step is visibly doing something between documents. */}
                {isNow && s.key === "extracting" && (
                  <div className="mt-1.5">
                    {/* "0 of 1" is not progress — it is a bar that sits at zero
                        for the entire wait and then finishes, which is the most
                        discouraging thing this card could show. A document count
                        only means something when there are documents to count,
                        so below two it gives way to the requirement total, which
                        climbs continuously. */}
                    <p className="text-[12.5px] text-[#77707E]">
                      {total > 1 && (
                        <>
                          {settled} of {total} documents
                          {found > 0 && " · "}
                        </>
                      )}
                      {found > 0 ? (
                        <span className="font-semibold tabular-nums text-[#A04A97]">
                          {shownFound} requirement{found === 1 ? "" : "s"} so far
                        </span>
                      ) : (
                        total <= 1 && "Reading the document"
                      )}
                    </p>
                    {total > 1 ? (
                      <div className="mt-1.5 h-[5px] overflow-hidden rounded-full bg-[#18101C]/[0.06]">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#A04A97] to-[#C86B4F] transition-[width] duration-700 ease-out motion-reduce:transition-none"
                          style={{ width: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                    ) : (
                      <Sweep />
                    )}
                  </div>
                )}

                {isNow && s.key === "checking" && <Sweep />}

                {/* Triage. Its own step because it is its own decision — which
                    requirements a knowledge base could ever answer — and it is
                    driven by state the server publishes, not by a timer. */}
                {isNow && s.key === "triaging" && (
                  <div className="mt-1.5">
                    <p className="text-[12.5px] leading-relaxed text-[#77707E]">
                      {found > 0
                        ? <>Checking which of <span className="font-semibold tabular-nums text-[#A04A97]">{shownFound}</span> requirements your knowledge base can answer</>
                        : "Checking which requirements your knowledge base can answer"}
                    </p>
                    <Sweep />
                  </div>
                )}

                {/* Once past it, say what it decided. A step that silently
                    removes requirements and reports nothing is the exact thing
                    that made the old score untrustworthy. */}
                {isDone && s.key === "triaging" && scoringTotal > 0 && (
                  <p className="mt-0.5 text-[12.5px] text-[#77707E]">
                    <span className="font-semibold tabular-nums text-[#3F3F46]">{scoringTotal}</span> to score
                    {excludedCount > 0 && (
                      <span className="text-[#92620B]"> · {excludedCount} set aside as administrative</span>
                    )}
                  </p>
                )}

                {/* Scoring: no server-side progress exists, so no bar pretends
                    to have one. The count it is working through, and a clock. */}
                {isNow && s.key === "scoring" && (
                  <div className="mt-1.5">
                    <p className="text-[12.5px] leading-relaxed text-[#77707E]">
                      {scoringTotal > 0
                        ? <>Searching for evidence on <span className="font-semibold tabular-nums text-[#A04A97]">{scoringTotal}</span> requirements</>
                        : "Searching your knowledge base for evidence"}
                    </p>
                    <Sweep />
                  </div>
                )}

                {isDone && s.key === "extracting" && found > 0 && (
                  <p className="mt-0.5 text-[12.5px] text-[#77707E]">
                    <span className="font-semibold tabular-nums text-[#3F3F46]">{shownFound}</span> found
                  </p>
                )}
                {isDone && s.key === "checking" && total > 0 && (
                  <p className="mt-0.5 text-[12.5px] text-[#77707E]">
                    {total} document{total === 1 ? "" : "s"}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {elapsed >= 10 && (
        <p className="mt-3 border-t border-[#18101C]/[0.06] pt-2.5 text-[12px] text-[#77707E] animate-copilot-rise motion-reduce:animate-none">
          This runs once per project — after that it's instant.
        </p>
      )}
    </div>
  );
};

export default CopilotQualifyProgress;
